import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import { AllTestCyclesResponse } from '../../types';
import {
  TestCyclesSatMetersRecord,
  UploadTestCycleCSVFilePayload,
  UploadTestCycleCSVFileResponse
} from '../../types/records/testCycles';

export const testCyclesEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'satApi'
  >
) => ({
  getAllTestCycles: builder.query<AllTestCyclesResponse, ''>({
    query: () => ({
      url: 'api/v2/testcycles'
    }),
    providesTags: ['getTestCycles']
  }),
  getTestCycleMeters: builder.query<TestCyclesSatMetersRecord[], string>({
    query: (param) => ({
      url: `api/v2/testcycles/meters?testCycleId=${param}`
    })
  }),
  uploadTestCyles: builder.mutation<
    UploadTestCycleCSVFileResponse,
    UploadTestCycleCSVFilePayload
  >({
    queryFn: async ({ formData, onProgress, signal }) => {
      const baseUrl = `${import.meta.env.VITE_SAT_URL}/api/v2/testcycles`;

      return new Promise((resolve, reject) => {
        // Initialize XMLHttpRequest
        const xhr = new XMLHttpRequest();

        // Open the request
        xhr.open('POST', baseUrl);

        // Set Authorization header if token exists
        const token = localStorage.getItem('accessToken');
        if (token) {
          xhr.setRequestHeader('Authorization', `Bearer ${token}`);
        }

        // Monitor the upload progress
        xhr.upload.onprogress = function (e: ProgressEvent<EventTarget>) {
          const percentComplete = Math.ceil((e.loaded / e.total) * 100);
          onProgress(percentComplete); // Update progress
        };

        // Handle successful response
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ data: JSON.parse(xhr.responseText) });
          } else {
            reject(new Error(xhr.responseText || 'Unknown upload error'));
          }
        };

        // Handle errors during the request
        xhr.onerror = () => {
          reject(new Error(xhr.responseText || 'Unknown upload error'));
        };

        // Listen for request cancellation via AbortSignal
        signal.addEventListener('abort', () => {
          xhr.abort(); // Abort the XMLHttpRequest
          reject({ error: { status: xhr.status, data: 'Upload aborted!' } });
        });

        // Send the formData to the server
        xhr.send(formData);
      });
    },
    invalidatesTags: ['getTestCycles']
  })
});
